import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, styled } from "@mui/material";
import StewartTable, {
  DetailsViewConfig,
  SelectionConfig,
  StewartTableColumn,
} from "controls/global/stewart-table/StewartTable";
import { EmptyStateType } from "controls/global/stewart-table/StewartTableBodyEmpty";
import StewartTableFooterPaginator from "controls/global/stewart-table/StewartTableFooterPaginator";
import { Order } from "controls/global/stewart-table/stewartTableHelper";
import { PremiumReceivablesTransactionRecord } from "entities/UIModel/PremiumReceivablesTransactionRecord";
import { colors, padding } from "theme/defaultStyle";
import { useSteppedProgress } from "utils/custom-hooks/useSteppedProgress";
import StatsBar, { StatsBarMetric } from "pages/utils/StatsBar";
import { formatCurrency } from "pages/utils/pageHelper";
import { PR_ROW_HEADER_WIDTH } from "./prColumns";
import PolicyLineItemsPanel from "./PolicyLineItemsPanel";
import { PolicyLineItemsCache } from "./usePolicyLineItems";

// Only Register transactions have policy detail worth drawing; every other
// type renders without a chevron.
const EXPANDABLE_TYPE_CODE = "REG";

// Wrapper for the drawer body. StewartTable renders the details row as a single
// cell spanning every column with `padding: 0`, so the top border and the left
// indent have to be supplied here. The indent is not cosmetic: the row-header
// gutter is sticky at `left: 0`, so content starting at the table's left edge
// would slide underneath it once the (wide) grid is scrolled horizontally.
const DetailsContainer = styled(Box)({
  padding: padding.zero,
  paddingLeft: `${PR_ROW_HEADER_WIDTH}px`,
  backgroundColor: colors.grey10,
  borderTop: `1px solid ${colors.grey09}`,
});

interface TransactionsTableProps {
  records: PremiumReceivablesTransactionRecord[];
  columns: StewartTableColumn<PremiumReceivablesTransactionRecord>[];
  hiddenColumns: (keyof PremiumReceivablesTransactionRecord)[];
  onColumnResize: (
    property: keyof PremiumReceivablesTransactionRecord,
    newWidth: number
  ) => void;
  onColumnsModified: (
    next: StewartTableColumn<PremiumReceivablesTransactionRecord>[]
  ) => void;
  rowsPerPage?: number;
  onRowsPerPageChange?: (count: number) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  totalRows?: number;
  totalPages?: number;
  isLoading?: boolean;
  sortColumn?: string;
  onSortChange?: (next: string) => void;
  hasSearched?: boolean;
  selectedIds: string[];
  onToggleRow: (row: PremiumReceivablesTransactionRecord) => void;
  onToggleAllRows: () => void;
  expandedRowIds: string[];
  onExpandedRowIdsChange: (ids: string[]) => void;
  // Needed by the drawer's policy-history fetch, not by the grid itself.
  userTimeZone: string;
}

const TransactionsTable = ({
  records,
  columns,
  hiddenColumns,
  onColumnResize,
  onColumnsModified,
  rowsPerPage: rowsPerPageProp,
  onRowsPerPageChange,
  page: pageProp,
  onPageChange,
  totalRows,
  totalPages,
  isLoading,
  sortColumn,
  onSortChange,
  hasSearched,
  selectedIds,
  onToggleRow,
  onToggleAllRows,
  expandedRowIds,
  onExpandedRowIdsChange,
  userTimeZone,
}: TransactionsTableProps) => {
  const [internalPage, setInternalPage] = useState<number>(1);
  const loadingPercent = useSteppedProgress(!!isLoading);
  const rowsPerPage = rowsPerPageProp ?? 10;

  // Policy line items already fetched for the open drawers. A ref rather than
  // state: writes must not re-render the grid, and the panel reads it during
  // its own first render. Reset needs no handling here — TransactionsTab
  // remounts this component via `key={resetCounter}`, discarding the ref.
  const lineItemsCacheRef = useRef<PolicyLineItemsCache>(new Map());

  // Fresh server data supersedes the drawers, so any new result set — page,
  // sort, Apply Filters, a post-save refetch — drops what was cached for the
  // old one. `records` keeps its identity between fetches (the list hook only
  // replaces it in the fetch response and in the disabled branch, which bails
  // out when already empty), so this fires exactly once per result set.
  useEffect(() => {
    lineItemsCacheRef.current.clear();
  }, [records]);

  // Derive the visual sort indicator from the controlled sortColumn string
  // ("-Foo" → desc Foo; "Foo" → asc Foo).
  const orderBy = useMemo<
    keyof PremiumReceivablesTransactionRecord | undefined
  >(() => {
    if (!sortColumn) return undefined;
    const col = sortColumn.startsWith("-") ? sortColumn.slice(1) : sortColumn;
    return col as keyof PremiumReceivablesTransactionRecord;
  }, [sortColumn]);
  const order: Order = sortColumn?.startsWith("-") ? "desc" : "asc";

  // Controlled page when the prop is provided (server-side paging from the
  // loader); otherwise internal state.
  const page = pageProp ?? internalPage;
  const useServerPaging = pageProp !== undefined && totalRows !== undefined;

  const handlePageChange = (newPage: number) => {
    setInternalPage(newPage);
    onPageChange?.(newPage);
  };
  const handleRowsPerPageChange = (count: number) => {
    setInternalPage(1);
    onRowsPerPageChange?.(count);
  };
  const handleRequestSort = (
    property: keyof PremiumReceivablesTransactionRecord
  ) => {
    const propStr = String(property);
    const currentColumn = sortColumn?.startsWith("-")
      ? sortColumn.slice(1)
      : sortColumn;
    const currentDesc = sortColumn?.startsWith("-") ?? false;
    const nextDesc = currentColumn === propStr ? !currentDesc : false;
    onSortChange?.(nextDesc ? `-${propStr}` : propStr);
  };

  // Three-way, so landing on the tab reads "no criteria entered" rather than
  // the misleading "no results found" a search never ran to produce.
  const emptyStateType = !hasSearched
    ? EmptyStateType.NO_FILTERS
    : records.length === 0
    ? EmptyStateType.NO_RESULTS
    : EmptyStateType.DEFAULT;
  const noRowsMessage = [
    "No Premium Receivables entries match the current filter.",
  ];

  const firstRecord = records[0];
  const statsMetrics = useMemo<StatsBarMetric[]>(() => {
    const num = (v: number | null | undefined) => (v ?? 0).toLocaleString();
    const cur = (v: number | null | undefined) => formatCurrency(v ?? 0);

    return [
      { label: "Liability", value: cur(firstRecord?.TotalLiabilityAmount) },
      { label: "S.I. Liability", value: cur(firstRecord?.TotalSILiabilityAmount) },
      { label: "Gross Premium", value: cur(firstRecord?.TotalActualFee) },
      { label: "Remit Basis", value: cur(firstRecord?.TotalActualRiskRate) },
      { label: "UW Retention", value: cur(firstRecord?.TotalActualRemittance) },
      { label: "# Policies", value: num(firstRecord?.TotalPolicyCount) },
      { label: "# S.I. Policies", value: num(firstRecord?.TotalSIPolicyCount) },
    ];
  }, [firstRecord]);

  // `selectedRows` and `isRowSelected` are both required: the row checkbox reads
  // isRowSelected, while the header's select-all / indeterminate math reads only
  // selectedRows. No isRowDisabled — every Premium Receivable row is selectable.
  const selectionConfig: SelectionConfig<PremiumReceivablesTransactionRecord> =
    useMemo(
      () => ({
        enabled: true,
        selectedRows: selectedIds,
        onToggleAllRows,
        onToggleRow,
        isRowSelected: (row: PremiumReceivablesTransactionRecord) =>
          selectedIds.includes(row.uniqueIdentifier),
      }),
      [selectedIds, onToggleAllRows, onToggleRow]
    );

  // Controlled expansion so Reset can collapse every drawer. Note this is
  // effectively write-only: StewartTable also gates the drawer on an internal
  // instance key that only its own chevron clicks populate, so pushing an id in
  // from here would not open a row.
  const detailsViewConfig: DetailsViewConfig<PremiumReceivablesTransactionRecord> =
    useMemo(
      () => ({
        enabled: true,
        hideExpandAllButton: true,
        isRowExpandable: (row: PremiumReceivablesTransactionRecord) =>
          row.PremiumReceivableTypeCode === EXPANDABLE_TYPE_CODE,
        renderRowDetails: (row: PremiumReceivablesTransactionRecord) => (
          <DetailsContainer>
            <PolicyLineItemsPanel
              row={row}
              userTimeZone={userTimeZone}
              cache={lineItemsCacheRef.current}
            />
          </DetailsContainer>
        ),
        expandedRowIds,
        onExpandedRowIdsChange,
      }),
      [expandedRowIds, onExpandedRowIdsChange, userTimeZone]
    );

  return (
    <Box
      sx={{
        // Beats the shared 55px .row-header-cell rule, which only fits one
        // control — this gutter holds a chevron and a checkbox. Applied via sx
        // rather than the rowHeaderWidth prop so it reaches the body cells too.
        "& .MuiTableCell-root.row-header-cell": {
          width: `${PR_ROW_HEADER_WIDTH}px !important`,
          minWidth: `${PR_ROW_HEADER_WIDTH}px`,
          maxWidth: `${PR_ROW_HEADER_WIDTH}px`,
        },
      }}
    >
      <StewartTable
        cols={columns}
        rows={records}
        page={page}
        rowsPerPage={rowsPerPage}
        showActionRow={true}
        noRowsMessage={noRowsMessage}
        orderBy={orderBy}
        order={order}
        useDbPagingSorting={useServerPaging}
        onRequestSort={handleRequestSort}
        onColumnResize={onColumnResize}
        onColumnsModified={onColumnsModified}
        hiddenColumns={hiddenColumns}
        selectionConfig={selectionConfig}
        detailsViewConfig={detailsViewConfig}
        emptyStateType={emptyStateType}
        footerComponent={
          <StatsBar
            highlight={{
              label: "Records",
              value: (firstRecord?.TotalRows ?? 0).toLocaleString(),
            }}
            metrics={statsMetrics}
          />
        }
        loadingProgressPercent={loadingPercent}
      />
      <StewartTableFooterPaginator
        totalRows={totalRows ?? records.length}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        disablePagination={(totalRows ?? records.length) === 0}
      />
    </Box>
  );
};

export default TransactionsTable;
